// src/beta/components/DossierTitleBlock.tsx
import type { V1Anchor, V1Verification } from '@/beta/types/dossier';

interface Props {
  vehicleName: string;
  tier: V1Verification['tier'];
  coveragePercent: number;
  anchor: V1Anchor | null;
}

const TIER_COLOR: Record<V1Verification['tier'], string> = {
  vaulted: 'var(--vm-tier-vaulted)',
  documented: 'var(--vm-tier-documented)',
  partial: 'var(--vm-tier-partial)',
  stub: 'var(--vm-tier-stub)',
};

function formatAnchorStamp(a: V1Anchor): string {
  const date = new Date(a.anchored_at);
  const month = date.toLocaleString('en', { month: 'short' }).toUpperCase();
  const year = date.getFullYear();
  const root = a.merkle_root.startsWith('0x') ? a.merkle_root : `0x${a.merkle_root}`;
  return `ANCHORED ${month} ${year} · ${root.slice(0, 5)}…`;
}

/** § 6.4 (1): Title block for the Dossier.
 *  Display-64 Oswald name, tier badge, coverage %, and merkle anchor stamp. */
export function DossierTitleBlock({ vehicleName, tier, coveragePercent, anchor }: Props) {
  return (
    <header style={{ paddingBottom: 'var(--vm-s-4)' }}>
      <h1
        style={{
          fontFamily: 'var(--vm-font-heading)',
          fontSize: 64,
          lineHeight: 1.05,
          fontWeight: 500,
          letterSpacing: '0.01em',
          color: 'var(--vm-warm-white)',
          margin: 0,
        }}
      >
        {vehicleName}
      </h1>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--vm-s-2)',
          marginTop: 'var(--vm-s-2)',
          fontFamily: 'var(--vm-font-heading)',
          fontSize: 13,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
        }}
      >
        {/* Tier + coverage metadata — single element so getByText finds exactly one match */}
        <span
          style={{
            color: TIER_COLOR[tier],
            border: `1px solid ${TIER_COLOR[tier]}`,
            padding: '4px 10px',
          }}
        >
          {tier.toUpperCase()} — {coveragePercent}%
        </span>
      </div>

      <div
        style={{
          marginTop: 'var(--vm-s-2)',
          fontFamily: 'var(--vm-font-heading)',
          fontSize: 11,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: anchor ? 'var(--vm-red)' : 'var(--vm-stone)',
        }}
      >
        {anchor ? formatAnchorStamp(anchor) : 'Not anchored'}
      </div>
    </header>
  );
}
